import { pick, fromPairs, isEmpty, has } from 'lodash';

import munge from '@datalas/data-munger';
import breakdownTokenize from '../../utils/breakdownTokenize';

import { DisplayGroupBreakdownType, FieldAttributeType } from '../../constants';
import BreakdownAugmenter from '../../lib/breakdown-augmenter';

export default {
  selectedBreakdownId: (state) => state.selectedBreakdownId,

  breakdownById: ({ breakdowns }) => (breakdownId) => {
    return has(breakdowns, breakdownId)
      ? breakdowns[breakdownId]
      : new BreakdownAugmenter().get({ attributes: [] });
  },

  activeGeoType: (state) => {
    return state.activeGeoType;
  },

  filtersByBreakdownId: (_, { breakdownById }, __, rootGetters) => (
    breakdownSlug,
  ) => {
    const breakdown = breakdownById(breakdownSlug);
    const { contexts } = breakdown;

    // Any context that is also used in dimensions is not a filter
    const transformsById = Object.fromEntries(
      breakdown.transforms.map((t) => [t.id, t]),
    );
    const dimensionFieldNames = new Set(
      breakdown.dimensions
        .map((d) => transformsById[d.transform])
        .map((t) => (t.field || '').replace(/(_label|_color)$/, '')),
    );

    const filtersByType = rootGetters['indicator/filtersByType'];
    const defaults = fromPairs(
      contexts.map(({ field, keys, defaultValue }) => [
        field,
        defaultValue || String(keys[0]),
      ]),
    );

    return contexts
      .filter(({ field }) => !dimensionFieldNames.has(field))
      .map(({ field, keys }) => {
        const fieldFromContexts = contexts.find(
          (context) => context.field === field,
        );
        const filter = {
          options: [],
          ...filtersByType[field],
          label: fieldFromContexts.label ?? filtersByType[field]?.label,
          defaultValue: defaults[field],
        };

        // Restrict the options to just the ones set on the breakdown
        if (keys && filter.options) {
          filter.options = keys.map((key) => {
            const option = filtersByType[field]
              ? filtersByType[field].options.find((o) => o.name === key) || {}
              : {};
            return {
              name: `${field}:${option.name}`,
              label: option.label || option.name,
              color: option.color || null,
            };
          });
        }

        return filter;
      });
  },

  contextByBreakdownId: ({ args }, { filtersByBreakdownId }) => (
    breakdownId,
  ) => {
    const filters = filtersByBreakdownId(breakdownId);

    const defaults = fromPairs(
      filters.map(({ name, defaultValue }) => [name, defaultValue]),
    );
    const keys = filters.map(({ name }) => name);

    return Object.assign(defaults, pick(args, keys));
  },

  fallbackContextByBreakdownId: (_, { breakdownById }) => (breakdownSlug) => {
    const breakdown = breakdownById(breakdownSlug);
    const { queryFilters } = breakdown;

    const fallbackContexts = fromPairs(
      Object.keys(queryFilters)
        .filter((key) => key !== 'geo_type')
        .map((name) => [name, queryFilters[name][0]]),
    );

    return fallbackContexts;
  },

  labelAttributesByBreakdownId: ({ breakdownsAttributes }) => (breakdownId) => {
    const attributes = breakdownsAttributes[breakdownId] || [];
    return attributes.filter((attr) => attr.type === FieldAttributeType.LABEL);
  },

  translationsByBreakdownId: (_, { filtersByBreakdownId }) => (breakdownId) => {
    const filters = filtersByBreakdownId(breakdownId);

    return filters
      .reduce(
        (acc, { name, label, options }) => [
          ...acc,
          { name, label },
          ...options,
        ],
        [],
      )
      .reduce((acc, item) => ({ ...acc, [item.name]: item.label }), {});
  },

  contextLabels(_, { contextByBreakdownId, translationsByBreakdownId }) {
    return (breakdownId) => {
      const translations = translationsByBreakdownId(breakdownId);
      const context = contextByBreakdownId(breakdownId);

      return fromPairs(
        Object.entries(context).map(([key, value]) => [
          key,
          translations[`${key}:${value}`],
        ]),
      );
    };
  },

  /**
   * TODO: review needs for 'geo_name_short' and 'geo_name_short_cmp'
   */
  tokenContextByBreakdownId(state, getters) {
    return (breakdownSlug) => {
      const breakdown = getters.breakdownById(breakdownSlug);
      const sliderContext = getters['mapFilters/sliderContext'] || {};
      const sliderLabel = sliderContext.tokenLabel || sliderContext.label;
      const geoName =
        state.primaryGeo.text ||
        state.primaryGeo.geo_display_name ||
        state.primaryGeo.geo_name;
      const secondaryGeoName =
        state.secondaryGeo.text ||
        state.secondaryGeo.geo_display_name ||
        state.secondaryGeo.geo_name;

      return {
        // Contexts
        ...getters.contextLabels(breakdownSlug),
        // Geo info
        geo_code: state.primaryGeo.id,
        geo_name: geoName,
        geo_name_short: state.primaryGeo.geo_name_short || geoName,
        // Geo comparison info
        geo_code_cmp: state.secondaryGeo.id,
        geo_name_cmp: secondaryGeoName,
        geo_name_short_cmp:
          state.secondaryGeo.geo_name_short || secondaryGeoName,
        // Other useful state
        value_field: breakdown.valueField,
        mouseover_field: breakdown.mouseoverField,
        slider_label: sliderLabel,
        geo_type:
          (
            (breakdown.geoTypeFilters || []).find(
              ({ value }) => value === state.geoType,
            ) || {}
          ).text || state.geoSection.text,
      };
    };
  },

  allDataByBreakdownId: (
    { breakdownsData, configs },
    _,
    { indicator },
    rootGetters,
  ) => (breakdownId) => {
    const config = configs[breakdownId];
    if (isEmpty(config)) return [];

    const breakdownConfig = config;
    const breakdownData = breakdownsData[breakdownId] || [];
    const filters = rootGetters['indicator/filtersByType'];
    const munged = munge(breakdownData, filters, breakdownConfig);

    // Apply label/color overrides to data:
    [
      ['label', 'fieldNameOverrides'],
      ['color', 'fieldColorOverrides'],
    ]
      // Get any "label" and "color" overrides from build instructions data:
      .map(([type, key]) => [
        type,
        (((indicator || {}).indicator || {}).config || {})[key],
      ])
      .filter(([, overrides]) => overrides && overrides.length)
      // Apply the overrides for each type (label, color):
      .forEach(([type, overrides]) => {
        overrides
          // Change, for example, { key: "raceth:01", value: "vanilla" } to
          // { fieldKey: "raceth", fieldValue: "01", override: "vanilla" }
          .map(({ key, value }) => {
            const [fieldKey, fieldValue] = key.split(':');
            return { fieldKey, fieldValue, override: value };
          })
          .forEach(({ fieldKey, fieldValue, override }) => {
            munged
              .filter((d) => d[fieldKey] === fieldValue)
              .forEach((d) => {
                // eslint-disable-next-line no-param-reassign
                d[`${fieldKey}_${type}`] = override;
              });
          });
      });

    // The backend always returns the same set of fields even if they're
    // undefined, it just casts them to null. This means the front end has no
    // way of differentiating between null and undefined. Ideally, data that is
    // unknown should be undefined and data that is known but missing should be
    // null. In the meantime, we have no choice but to strip out both.

    // Examples:
    // - "By gender" male vs. female comparison charts are sometimes missing one
    //   value but not the other; these should be (and currently are) null
    // - "By ancestry" fetches a ton of racethd's that have no values in the
    //   system; these should be undefined (but are currently null)

    const field = breakdownConfig.valueField || 'data_column';
    const data = munged.filter(
      (datum) => datum[field] !== null && datum[field] !== undefined,
    );

    return Object.freeze(data);
  },

  dataByBreakdownId: (
    { configs, primaryGeo },
    { allDataByBreakdownId, contextByBreakdownId },
  ) => (breakdownId) => {
    const config = configs[breakdownId];

    if (isEmpty(config)) return [];

    const breakdownConfig = config;
    const breakdownAllData = allDataByBreakdownId(breakdownId);
    const context = contextByBreakdownId(breakdownId);

    const contextKeys = Object.keys(context);
    const data = Object.freeze(
      breakdownAllData.filter((datum) =>
        contextKeys.every((field) => {
          return String(datum[field]) === String(context[field]);
        }),
      ),
    );

    // https://gitlab.com/policy-link/data_atlas/-/issues/179
    // On rank charts, if there's no bar for the selected geo, display no data
    // at all
    const primaryGeoCode = (primaryGeo || {}).id;
    if (
      breakdownConfig.subtype === 'rank' &&
      (!primaryGeoCode || !data.find((d) => d.geo_code_long === primaryGeoCode))
    ) {
      return [];
    }

    return data;
  },

  captionByBreakdownId(
    { secondaryGeo },
    { breakdownById, dataByBreakdownId, tokenContextByBreakdownId },
  ) {
    return (breakdownId) => {
      const breakdown = breakdownById(breakdownId);
      const data = dataByBreakdownId(breakdownId);
      const tokenContext = tokenContextByBreakdownId(breakdownId);

      const { CAPTION, CAPTION_CMP } = breakdown.statements;
      return [CAPTION, (secondaryGeo.geo_code && CAPTION_CMP) || undefined]
        .filter((x) => x)
        .map(breakdownTokenize(breakdown, data, tokenContext))
        .join(' ');
    };
  },

  mapSliderCaptionByBreakdownId(
    _,
    { breakdownById, dataByBreakdownId, tokenContextByBreakdownId },
  ) {
    return (breakdownId) => {
      const breakdown = breakdownById(breakdownId);
      const data = dataByBreakdownId(breakdownId);
      const tokenContext = tokenContextByBreakdownId(breakdownId);

      const { MAP_SLIDER_CAPTION } = breakdown.statements;
      return (
        MAP_SLIDER_CAPTION &&
        breakdownTokenize(breakdown, data, tokenContext)(MAP_SLIDER_CAPTION)
      );
    };
  },

  // TODO: move map specific getters out of breakdown store
  minValue: (
    { geoType },
    { breakdownById, contextByBreakdownId, fallbackContextByBreakdownId },
  ) => {
    const breakdownSlug = DisplayGroupBreakdownType.MAP;
    const breakdown = breakdownById(breakdownSlug);
    const context = contextByBreakdownId(breakdownSlug);

    let finalContexts = context;
    if (isEmpty(context)) {
      finalContexts = fallbackContextByBreakdownId(breakdownSlug);
    }

    if (geoType && breakdown && breakdown.minMax && breakdown.minMax[geoType]) {
      // eslint-disable-next-line no-confusing-arrow
      const filterCombo = Object.keys(finalContexts)
        .map((ea) => `${ea}:${finalContexts[ea]}`)
        .sort((a, b) => (a.toUpperCase() < b.toUpperCase() ? -1 : 1))
        .join('-');
      const minMax = breakdown.minMax[geoType][filterCombo];
      if (minMax) {
        return minMax.min;
      }
      return null;
    }

    return null;
  },
  maxValue: (
    { geoType },
    { breakdownById, contextByBreakdownId, fallbackContextByBreakdownId },
  ) => {
    const breakdownSlug = DisplayGroupBreakdownType.MAP;
    const breakdown = breakdownById(breakdownSlug);
    const context = contextByBreakdownId(breakdownSlug);

    let finalContexts = context;
    if (isEmpty(context)) {
      finalContexts = fallbackContextByBreakdownId(breakdownSlug);
    }

    if (geoType && breakdown && breakdown.minMax && breakdown.minMax[geoType]) {
      // eslint-disable-next-line no-confusing-arrow
      const filterCombo = Object.keys(finalContexts)
        .map((ea) => `${ea}:${finalContexts[ea]}`)
        .sort((a, b) => (a.toUpperCase() < b.toUpperCase() ? -1 : 1))
        .join('-');
      const minMax = breakdown.minMax[geoType][filterCombo];
      if (minMax) {
        return minMax.max;
      }
      return null;
    }

    return null;
  },

  transformsByBreakdownId: (
    _,
    { breakdownById },
    { indicator: { indicator } },
  ) => (breakdownId) => {
    const breakdown = breakdownById(breakdownId);

    return [...(indicator.transforms || []), ...(breakdown.transforms || [])];
  },

  getGeo: (_state, _getters, _rootState, rootGetters) => (geoCode) => {
    return (
      (rootGetters['indicator/geoSections'] || [])
        .flatMap((section) => section.children)
        .find(({ id }) => id === geoCode) || {}
    );
  },

  primaryGeo: (_, getters) => getters.getGeo(getters['query/primaryGeoCode']),

  secondaryGeo: (_, getters) =>
    getters.getGeo(getters['query/secondaryGeoCode']),
};
